import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

function extractLocation(groups) {
  // Expected groups: ["Branch Manager","221 Silverwater"] etc.
  if (!groups) return "";

  if (Array.isArray(groups)) {
    return String(groups[1] || "").trim();
  }

  if (typeof groups === "string") {
    try {
      const parsed = JSON.parse(groups);
      if (Array.isArray(parsed)) return String(parsed[1] || "").trim();
    } catch {
      // ignore
    }
  }

  return "";
}

export default function Profile() {
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Learner");
  const [groups, setGroups] = useState(null); // can be array or string or null

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: string }

  const location = useMemo(() => extractLocation(groups), [groups]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setMessage(null);

      try {
        const { data: userRes, error: userErr } = await supabase.auth.getUser();
        if (userErr || !userRes?.user) {
          throw new Error("Not logged in.");
        }

        const user = userRes.user;
        if (cancelled) return;

        setEmail(user.email || "");

        // Load role + groups from profiles
        const { data: prof, error: profErr } = await supabase
          .from("profiles")
          .select("role, groups")
          .eq("user_id", user.id)
          .single();

        if (!cancelled) {
          if (!profErr && prof?.role) setRole(prof.role);
          else setRole("Learner");

          // groups might be array/json or null
          setGroups(prof?.groups ?? null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMessage({ type: "error", text: "Failed to load profile." });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePasswordReset = async () => {
    setMessage(null);

    const p1 = newPassword.trim();
    const p2 = confirmPassword.trim();

    if (!p1 || !p2) {
      setMessage({ type: "error", text: "Please complete both password fields." });
      return;
    }

    if (p1 !== p2) {
      setMessage({ type: "error", text: "Passwords do not match." });
      return;
    }

    if (p1.length < 8) {
      setMessage({ type: "error", text: "Password must be at least 8 characters." });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: p1 });
      if (error) throw error;

      setNewPassword("");
      setConfirmPassword("");
      setMessage({ type: "success", text: "Password updated successfully." });
    } catch (e) {
      console.error(e);
      setMessage({
        type: "error",
        text: e?.message || "Failed to update password.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    setMessage(null);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      window.location.href = "/login";
    } catch (e) {
      console.error(e);
      setMessage({ type: "error", text: "Failed to log out." });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white shadow rounded-xl p-6 text-sm text-gray-600">
          Loading profile…
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      {/* Account Info */}
      <div className="bg-white shadow rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Account Details</h2>

        <div className="space-y-2 text-sm text-gray-700">
          <p>
            <span className="font-semibold">Email:</span> {email || "—"}
          </p>

          <p>
            <span className="font-semibold">Role:</span>{" "}
            {role ? role.charAt(0).toUpperCase() + role.slice(1) : "Learner"}
          </p>

          {/* Sanity check: groups */}
          <p>
            <span className="font-semibold">Groups:</span>{" "}
            {Array.isArray(groups)
              ? groups.join(" • ")
              : groups
              ? String(groups)
              : "—"}
          </p>

          {/* Helpful: extracted location */}
          <p>
            <span className="font-semibold">Location (derived):</span>{" "}
            {location || "—"}
          </p>
        </div>
      </div>

      {/* Password Reset */}
      <div className="bg-white shadow rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Reset Password</h2>

        <div className="space-y-4">
          <input
            type="password"
            placeholder="New Password (min 8 characters)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full border border-gray-300 rounded px-4 py-2 text-sm"
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full border border-gray-300 rounded px-4 py-2 text-sm"
          />

          {message && (
            <div
              className={`text-sm ${
                message.type === "success" ? "text-green-600" : "text-red-600"
              }`}
            >
              {message.text}
            </div>
          )}

          <button
            onClick={handlePasswordReset}
            disabled={saving}
            className="bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium py-2 px-4 rounded disabled:opacity-50"
          >
            {saving ? "Updating…" : "Reset Password"}
          </button>
        </div>
      </div>

      {/* Logout */}
      <div className="flex justify-end">
        <button
          onClick={handleLogout}
          className="text-sm text-red-600 hover:text-red-800 underline"
          type="button"
        >
          Log Out
        </button>
      </div>
    </div>
  );
}
